import { useEffect } from 'react';
import { create } from 'zustand';

export type ToastTone = 'default' | 'success' | 'error';

export interface ToastItem {
  id: string;
  message: string;
  tone: ToastTone;
  durationMs: number;
}

interface ToastState {
  toasts: ToastItem[];
  show: (message: string, opts?: { tone?: ToastTone; durationMs?: number }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const DEFAULT_DURATION = 2500;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  show: (message, opts) => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `t_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const item: ToastItem = {
      id,
      message,
      tone: opts?.tone ?? 'default',
      durationMs: opts?.durationMs ?? DEFAULT_DURATION,
    };
    set((s) => ({ toasts: [...s.toasts, item] }));
    return id;
  },
  dismiss: (id) =>
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

export function useToast() {
  const show = useToastStore((s) => s.show);
  return {
    show,
    success: (msg: string, durationMs?: number) =>
      show(msg, durationMs !== undefined ? { tone: 'success', durationMs } : { tone: 'success' }),
    error: (msg: string, durationMs?: number) =>
      show(msg, durationMs !== undefined ? { tone: 'error', durationMs } : { tone: 'error' }),
  };
}

// Airbnb toast — white surface + 3-layer shadow + 20px radius, near-black/ error / success text
const TONE_CLASS: Record<ToastTone, string> = {
  default: 'bg-surface text-ink',
  success: 'bg-surface text-success-strong',
  error: 'bg-surface text-error',
};

function ToastRow({ toast }: { toast: ToastItem }) {
  const dismiss = useToastStore((s) => s.dismiss);
  useEffect(() => {
    if (toast.durationMs <= 0) return;
    const t = window.setTimeout(() => dismiss(toast.id), toast.durationMs);
    return () => window.clearTimeout(t);
  }, [toast.id, toast.durationMs, dismiss]);
  return (
    <div
      role="status"
      className={`pointer-events-auto animate-[rise-in_0.32s_var(--ease-out-expo)_both] rounded-card px-5 py-3 text-body-md font-medium [box-shadow:var(--shadow-floating)] ${TONE_CLASS[toast.tone]}`}
    >
      {toast.message}
    </div>
  );
}

export function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  if (toasts.length === 0) return null;
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed bottom-[calc(74px+env(safe-area-inset-bottom))] left-1/2 z-50 flex -translate-x-1/2 flex-col items-center gap-2"
    >
      {toasts.map((t) => (
        <ToastRow key={t.id} toast={t} />
      ))}
    </div>
  );
}
